/* eslint-disable react/no-unstable-nested-components */
import {css, Typography, useTheme} from '@mui/material';
import {
  GetStylesFunction,
  NoDataMessageWithIcon,
  DlMuiChip,
  DlMuiDeleteButton,
  PopupWrapper,
  DlMuiButton,
  DlMuiAddButton,
} from '@wallaby-ui/core';
import classNames from 'classnames';
import {isEmpty} from 'lodash';
import * as React from 'react';
import {useMemo} from 'react';

import {usePermissions} from '../../permissions/usePermissions';
import {
  RISK_FACTOR_LABEL_COLORS,
  getRiskFactorMedicationLabel,
  groupAndSortRiskFactorsData,
  RiskFactorGroup,
} from '../hospitalizations_helpers';
import {TooltipWrapper, useFormatters} from 'src/js/common';
import {MerieSchema} from 'src/js/common/types';

interface OwnProps {
  config: any;
  riskFactors: MerieSchema.components['schemas']['RiskFactorDto'][];
  riskFactorsEtag: string | undefined;
  oldRiskFactorIds: Set<number>;
  changeRiskFactorFilter: (newRiskFactorId: number) => void;
  isDeactivated: boolean;
  activateOrDeactivateRiskFactor: (riskFactorId: string, isDeactivated: boolean) => Promise<void>;
}

type Props = OwnProps;

export const DiagnosisRiskFactors: React.FC<Props> = (props) => {
  const {
    config,
    riskFactors,
    riskFactorsEtag: _,
    oldRiskFactorIds,
    changeRiskFactorFilter,
    isDeactivated,
    activateOrDeactivateRiskFactor,
  } = props;

  const theme = useTheme();
  const styles = getStyles({theme});
  const {t, tEnum, formatDate} = useFormatters();

  const groupedRows = useMemo(() => {
    const translatedRiskFactors = riskFactors.map((rf) => {
      const translatedRiskFactorType = tEnum('RiskFactorTypeEnum', rf.type);
      return {
        ...rf,
        translatedTypeOrUserComment:
          rf.type === 'OTHER' ? `${translatedRiskFactorType} - ${rf.typeOtherUserGivenName}` : translatedRiskFactorType,
        narrowTherapeuticIndexMedication: tEnum('NarrowTherapeuticIndexMedicationEnum', rf?.narrowTherapeuticIndexMedication),
      };
    });
    return groupAndSortRiskFactorsData(translatedRiskFactors, oldRiskFactorIds);
  }, [riskFactors, tEnum, oldRiskFactorIds]);

  const [selectedRiskFactorToEdit, setSelectedRiskFactorToEdit] = React.useState(undefined as any);
  const [riskFactorToEditAnchorTag, setRiskFactorToEditAnchorTag] = React.useState(null as HTMLElement | null);
  const [groupToDeactivate, setGroupToDeactivate] = React.useState<RiskFactorGroup | null>(null);
  const [groupToDeactivateAnchorTag, setGroupToDeactivateAnchorTag] = React.useState<HTMLElement | null>(null);
  const riskFactorsWrapperRef = React.useRef(null as HTMLDivElement | null);

  const potentialAvailableSpaceBelow = 100;

  const handlePopperOpeningDirection = (anchorTag: HTMLElement | null, wrapperRef: React.RefObject<HTMLDivElement> | null) => {
    if (!anchorTag || !wrapperRef?.current) return false;
    const rect = anchorTag.getBoundingClientRect();
    return window.innerHeight - rect.bottom < potentialAvailableSpaceBelow;
  };

  const {userRoles} = usePermissions(config);
  const canEdit = userRoles.ENCOUNTER_DETAIL_EDIT_ROLE || false;

  const resetEditState = () => {
    setSelectedRiskFactorToEdit(null);
    setRiskFactorToEditAnchorTag(null);
  };

  const resetGroupDeactivateState = () => {
    setGroupToDeactivate(null);
    setGroupToDeactivateAnchorTag(null);
  };

  const renderRiskFactorRow = (riskFactor: any, options?: {hideLabel?: boolean; parentGroupType?: string}) => {
    const {hideLabel = false, parentGroupType} = options ?? {};
    const translatedRiskFactorLabels = tEnum('RiskFactorLabelEnum', riskFactor.labels[0]);
    const tooltipDateName = t(isDeactivated ? 'riskFactors.tooltip.inactive.date' : 'riskFactors.tooltip.active.date');
    const tooltipDateValue = riskFactor.stateLastModifiedOn ?? riskFactor.createdOn;
    const tooltipUserName = t(isDeactivated ? 'riskFactors.tooltip.inactive.user' : 'riskFactors.tooltip.active.user');
    const tooltipUserValue = riskFactor.stateLastModifiedBy ?? riskFactor.createdBy;
    const createdByString = (
      <div>
        <div>
          {tooltipDateName}: {formatDate(tooltipDateValue)}
        </div>
        <div>
          {tooltipUserName}: {tooltipUserValue}
        </div>
      </div>
    );
    const isSelectedToEdit = selectedRiskFactorToEdit?.id === riskFactor.id;

    const medicationLabel = getRiskFactorMedicationLabel(riskFactor);
    const translatedParentGroupType = parentGroupType ? tEnum('RiskFactorTypeEnum', parentGroupType as any) : null;

    const childTitle =
      medicationLabel ??
      (hideLabel && translatedParentGroupType && riskFactor.translatedTypeOrUserComment === translatedParentGroupType
        ? t('riskFactors.group.other')
        : riskFactor.translatedTypeOrUserComment);

    const buttonTitle = hideLabel ? childTitle : [riskFactor.translatedTypeOrUserComment, medicationLabel].filter((v) => v).join(' - ');

    return (
      <div
        css={styles.riskFactorWrapper}
        key={riskFactor.id}
        className={classNames('hideIfNotHoveredParent', isSelectedToEdit && 'selected')}
      >
        {hideLabel ? (
          <DlMuiChip
            label={translatedRiskFactorLabels || ''}
            inputCss={css`
              ${styles.riskFactorLabel};
              visibility: hidden;
            `}
          />
        ) : (
          <DlMuiChip
            label={translatedRiskFactorLabels || ''}
            inputCss={styles.riskFactorLabel}
            style={{backgroundColor: RISK_FACTOR_LABEL_COLORS[riskFactor.labels[0]]?.color}}
          />
        )}
        <TooltipWrapper tooltip={createdByString}>
          <DlMuiButton
            type='text-gray'
            regular={oldRiskFactorIds.has(riskFactor.id)}
            title={buttonTitle}
            onClick={riskFactor.createdBy === 'MERIE' ? () => changeRiskFactorFilter(riskFactor.id) : undefined}
            style={{color: 'inherit'}}
          />
        </TooltipWrapper>
        {canEdit && (
          <PopupWrapper
            anchorTag={isSelectedToEdit ? riskFactorToEditAnchorTag : null}
            direction={handlePopperOpeningDirection(riskFactorToEditAnchorTag, riskFactorsWrapperRef) ? 'up' : 'down'}
            openButton={
              isDeactivated ? (
                <DlMuiAddButton
                  onClick={(event) => {
                    setSelectedRiskFactorToEdit(riskFactor);
                    setRiskFactorToEditAnchorTag(event.nativeEvent.target as HTMLElement);
                  }}
                  iconTitle={'riskFactors.reactivate'}
                  className={isSelectedToEdit ? '' : 'hideIfNotHovered'}
                  isSelected={Boolean(selectedRiskFactorToEdit)}
                />
              ) : (
                <DlMuiDeleteButton
                  onClick={(event) => {
                    setSelectedRiskFactorToEdit(riskFactor);
                    setRiskFactorToEditAnchorTag(event.nativeEvent.target as HTMLElement);
                  }}
                  iconTitle={'riskFactors.deactivate'}
                  className={isSelectedToEdit ? '' : 'hideIfNotHovered'}
                  isSelected={Boolean(selectedRiskFactorToEdit)}
                />
              )
            }
            xOffset={-1}
            yOffset={6}
            showKeepDeleteButtons={!isDeactivated}
            showDiscardSaveButtons={isDeactivated}
            cancelTitle='common.confirmDialog.cancel'
            confirmTitle={isDeactivated ? 'riskFactors.reactivate' : 'riskFactors.deactivate'}
            onClose={resetEditState}
            onConfirm={() => {
              if (selectedRiskFactorToEdit) activateOrDeactivateRiskFactor(selectedRiskFactorToEdit.id, isDeactivated);
              resetEditState();
            }}
          >
            <Typography variant='sectionHeader'>
              {t(isDeactivated ? 'riskFactors.reactivate.message' : 'riskFactors.deactivate.message')}
            </Typography>
          </PopupWrapper>
        )}
      </div>
    );
  };

  const renderGroupRow = (group: RiskFactorGroup) => {
    const translatedGroupLabel = tEnum('RiskFactorLabelEnum', group.label);
    const translatedGroupType = tEnum('RiskFactorTypeEnum', group.groupType);
    const isGroupSelectedToDeactivate = groupToDeactivate?.groupType === group.groupType;

    return (
      <div key={group.groupType} css={styles.riskFactorGroupWrapper}>
        <div css={styles.riskFactorWrapper} className='hideIfNotHoveredParent'>
          <DlMuiChip
            label={translatedGroupLabel || ''}
            inputCss={styles.riskFactorLabel}
            style={{backgroundColor: RISK_FACTOR_LABEL_COLORS[group.label]?.color}}
          />
          <Typography css={styles.groupParentTitle}>{translatedGroupType}</Typography>
          {canEdit && !isDeactivated && (
            <PopupWrapper
              anchorTag={isGroupSelectedToDeactivate ? groupToDeactivateAnchorTag : null}
              direction={handlePopperOpeningDirection(groupToDeactivateAnchorTag, riskFactorsWrapperRef) ? 'up' : 'down'}
              openButton={
                <DlMuiDeleteButton
                  onClick={(event) => {
                    setGroupToDeactivate(group);
                    setGroupToDeactivateAnchorTag(event.nativeEvent.target as HTMLElement);
                  }}
                  iconTitle={'riskFactors.deactivate'}
                  className={isGroupSelectedToDeactivate ? '' : 'hideIfNotHovered'}
                  isSelected={Boolean(groupToDeactivate)}
                />
              }
              xOffset={-1}
              yOffset={6}
              showKeepDeleteButtons
              cancelTitle='common.confirmDialog.cancel'
              confirmTitle='riskFactors.deactivate'
              onClose={resetGroupDeactivateState}
              onConfirm={() => {
                if (groupToDeactivate) {
                  groupToDeactivate.children.forEach((child) => activateOrDeactivateRiskFactor(String(child.id), false));
                }
                resetGroupDeactivateState();
              }}
            >
              <Typography variant='sectionHeader'>{t('riskFactors.deactivateGroup.message')}</Typography>
            </PopupWrapper>
          )}
        </div>
        {group.children.map((child) =>
          renderRiskFactorRow(child, {
            hideLabel: true,
            parentGroupType: group.groupType,
          })
        )}
      </div>
    );
  };

  if (isEmpty(groupedRows)) {
    return <NoDataMessageWithIcon message={'hospitalization.detail.noRiskFactors'} />;
  }

  return (
    <div css={styles.riskFactorsWrapper} data-deactivated={isDeactivated} ref={riskFactorsWrapperRef}>
      {groupedRows.map((row) => ((row as RiskFactorGroup).isGroup ? renderGroupRow(row as RiskFactorGroup) : renderRiskFactorRow(row)))}
    </div>
  );
};

DiagnosisRiskFactors.displayName = 'DiagnosisRiskFactors';

const getStyles: GetStylesFunction = (options) => ({
  riskFactorsWrapper: css`
    display: flex;
    flex-direction: column;
    gap: 0px;
    color: var(--riskFactorLabel);
    --riskFactorLabel: ${options?.theme?.palette.custom.coolGray['800']};
    &[data-deactivated='true'] {
      --riskFactorLabelBackground: ${options?.theme?.palette.custom.coolGray['50']};
      --riskFactorLabel: ${options?.theme?.palette.custom.coolGray['400']};
    }
  `,
  riskFactorGroupWrapper: css`
    display: flex;
    flex-direction: column;
  `,
  riskFactorWrapper: css`
    padding: 2px 4px 2px 2px;
    border-radius: 4px;
    display: flex;
    flex-direction: row;
    gap: 4px;
    align-items: center;
    word-break: break-word;
    &.selected,
    &:hover {
      background: ${options?.theme?.palette.custom.coolGray['50']};
    }
  `,
  groupParentTitle: css`
    font-weight: 500;
  `,
  riskFactorLabel: css`
    background: var(--riskFactorLabelBackground);
    margin-right: 8px;
    color: var(--riskFactorLabel);
    & .MuiChip-label {
      color: var(--riskFactorLabel);
    }
  `,
});
